<template>
  <div class="text-justify row">
    <div class="col-xs-12">
      <p>
        Les résultats présentés sur cette page comportent les
        <strong>taux d'incidence</strong> des infections surveillées :
        bactériémies et pneumopathies.
      </p>
      <h3>Taux d'incidence</h3>
      <p>
        Les différents taux d'incidence rapportent l'incidence
        (nombre de nouveaux cas) à la taille de la population
        considérée.
      </p>
      <p>
        Le taux est exprimé en nombre de cas par personne-temps, par
        exemple pour 1000 journées d'hospitalisation ou pour 100
        admissions.
      </p>
      <table class="table table-responsive">
        <tbody>
          <tr v-for="i in sc.incidences"
              :key="i.key">
            <td class="text-left">
              <strong>
                {{ i.title }}
              </strong>
            </td>
            <td>
              <p v-html="i.text">
              </p>
              <p>
                <i>
                  Exemple: {{ i.example }}
                </i>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        La robustesse des valeurs d'incidence obtenues est variable. Les
        données auxquelles vous avez accès sont donc à considérer avec
        précaution, en particulier lorsqu'elles sont obtenues à partir
        d'un nombre d'établissements très limité.
      </p>
      <h3>Bactériémies</h3>
      <p>
        Seules les bactériémies acquises dans l'établissement
        (hospitalisation complète ou incomplète)
        sont incluses dans les calculs des taux d'incidence.
      </p>
      <p>
        Les valeurs d'incidence pour les bactériémies associées aux
        «autres cathéters centraux» pour 1000 journées cathéter ont été
        exclues en raison du manque de vraisemblance des dénominateurs.
      </p>
      <h3>Pneumopathies acquises sous intubation</h3>
      <p>
        Les pneumopathies sont considérées acquises sous
        intubation si la date d'épisode de la
        pneumopathie est postérieure à la date de début de
        l'intubation + 2 jours et antérieure à la date de fin de
        l'intubation + 2 jours.
      </p>
      <h3>Tableaux de taux d'incidence</h3>
      <p>
        L'onglet <strong>Tableaux</strong> de la page résultat permet
        de lister les taux d'incidence des bactériémies par porte
        d'entrée et des pneumopathies pour toutes les disciplines
        et toutes les UFs pour lesquelles les dénominateurs ont été
        renseignés dans la fiche établissement.
      </p>
      <p>
        Les <strong>sélecteurs</strong> permettent d'accéder aux
        différents tableaux. Le bouton <strong>Exporter en PDF</strong>
        permet de générer un fichier PDF contenant les tableaux liés
        aux disciplines et aux UFs choisies. En cliquant sur un
        taux d'incidence dans le tableau, vous pouvez obtenir
        le graphique de répartition des taux d'incidence correspondant.
      </p>
      <h5>
        Légende des valeurs d'incidences
      </h5>
      <table class="table table-responsive">
        <tbody>
          <tr class="legend" v-for="(l, k) in sc.legend"
              :key="`legend-${k}`">
            <td class="text-left">
              <strong>{{ l.title }}</strong>
              <br /><small>(moyenne nationale)</small>
            </td>
            <td class="text-center">
              <sui-incidence :value="l"></sui-incidence>
            </td>
            <td v-html="l.subtitle"></td>
          </tr>
        </tbody>
      </table>
      <h3>Graphiques de répartition des taux d'incidence</h3>
      <p>
        La partie <strong>Graphiques</strong> permet de générer et
        d'exporter des grahiques de répartition des taux d'incidence
        des bactériémies et des pneumopathies.
      </p>
      <p>
        Utilisez les <strong>sélecteurs</strong> pour
        générer un graphique initial. Dans un deuxième temps, vous
        pouvez comparer ce graphique avec d'autres portes d'entrée ou
        avec d'autres types d'établissements.
      </p>
      <div class="img-container text-center">
        <img class="img-responsive"
             :src="require('@/sui/assets/img/chart.jpg')" ref="target">
      </div>
      <p>
        Sur chaque graphique sont présentées les incidences
        de la sous population d'établissements sélectionnée
        à laquelle vous comparez
        votre valeur d'incidence (par type d'infection, discipline, porte
        d'entrée ou groupement de portes d'entrée, type de dénominateur et type
        d'établissement).
      </p>
      <p>
        Vous pouvez comparer les incidences de votre
        établissement aux populations de votre choix, situer votre
        valeur dans la distribution de la population, et détecter
        une potientielle anomalie.
      </p>
      <p>
        Le graphique contient aussi une <strong>boîte à moustache</strong>
        qui représente le profil de la population. Il résume des
        indicateurs de position tels que la médiane et les quartiles.
      </p>
      <p>
        La <strong>médiane</strong> est la valeur qui sépare la
        population en deux parts égales (i.e. la valeur telle que la
        moitié de la population lui est inférieure et l'autre moitié
        lui est supérieure). Elle est figurée par le trait horizontal
        à l'intérieur de la boîte.
      </p>
      <p>
        La <strong>boîte</strong> représente l'écart interquartile
        (Q3 - Q1). C'est une mesure de dispersion autour de la médiane.
        C'est la plage de valeurs contenant la moitié
        de la population sans les 25% de la population ayant les
        taux les plus bas (taux inférieur à <strong>Q1</strong>) et les 25% ayant les taux les plus
        élevés (taux supérieur à <strong>Q3</strong>).
      </p>
      <p>
        Les segments verticaux mènent jusqu'aux premier et neuvième
        déciles (ie les valeurs telles que 10% et 90% respectivement
        de la population lui est inférieure)
      </p>
      <span v-if="!hideContact">
        <hr>
        <p>
          N'hésitez pas à revenir vers nous via le
          <router-link to="/contact">formulaire de contact</router-link> si
          vous souhaitez avoir plus de précisions sur le fonctionnement
          de ce module.
        </p>
      </span>
    </div>
  </div>
</template>
<script>
import suiIncidence from '@/sui/components/results/incidence'
import sc from '@/sui/components/results/sc/readResults'

export default {
  name: 'how-to-read-results',

  props: {
    hideContact: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      sc: sc
    }
  },

  components: {
    'sui-incidence': suiIncidence
  }
}
</script>
<style scoped lang="less">
.legend {
  h5 {
    margin-bottom: 0.4rem;
  }
}

.img-container {
  img {
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
